/**
 * Die Geometrie der **Reise**: aus einer ausgependelten 3D-Punktwolke eine Flugbahn machen.
 *
 * Der Flug hängt bewusst **nicht am Graphen, sondern an seiner räumlichen Ausprägung**. Sobald das
 * Kräftespiel steht, ist die Information schon im Raum: dichte Ballungen sind zusammenhängende
 * Teilgraphen, Ausreißer hängen am Rand. Wer den Kanten folgte, flöge Zickzack durch dieselbe
 * Wolke; wer die Ballungen umrundet, zeigt ihre Struktur.
 *
 * Alles hier ist reine Rechnung auf Zahlen — testbar ohne Browser, ohne three.js, ohne Daten.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface JourneyPoint extends Vec3 {
  id: string;
  type: string;
  label: string;
}

export interface Cluster {
  /** Indizes in die übergebene Punktliste. */
  members: number[];
  centroid: Vec3;
  /** Robuster Radius: 95. Perzentil des Abstands zum Schwerpunkt, nie das Maximum. */
  radius: number;
  /** Häufigster Knotentyp und Anzahl — die Beschriftung der Ballung. */
  label: string;
  count: number;
}

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scale = (a: Vec3, f: number): Vec3 => ({ x: a.x * f, y: a.y * f, z: a.z * f });
export const length = (a: Vec3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
export const distance = (a: Vec3, b: Vec3): number => length(sub(a, b));
export const normalize = (a: Vec3): Vec3 => {
  const l = length(a);
  return l < 1e-9 ? { x: 0, y: 0, z: 1 } : scale(a, 1 / l);
};

export const centroidOf = (points: Vec3[]): Vec3 => {
  if (!points.length) return { x: 0, y: 0, z: 0 };
  const sum = points.reduce((carry, point) => add(carry, point), { x: 0, y: 0, z: 0 });
  return scale(sum, 1 / points.length);
};

/** Das p-Perzentil einer Zahlenreihe (0..1), linear interpoliert. */
export const percentile = (values: number[], p: number): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * Math.min(Math.max(p, 0), 1);
  const low = Math.floor(position);
  const high = Math.ceil(position);
  if (low === high) return sorted[low];
  return sorted[low] + (sorted[high] - sorted[low]) * (position - low);
};

/**
 * Der Median des Abstands zum nächsten Nachbarn — der **Maßstab der Wolke**.
 *
 * Jede Schwelle in dieser Datei leitet sich daraus ab, statt eine Konstante zu raten: eine
 * Kraftsimulation legt ihre Knoten je nach Anzahl und Abstoßung enger oder weiter, und ein fester
 * Wert wäre bei zehn Knoten zu klein und bei dreihundert zu groß.
 */
export const medianNearestNeighbourDistance = (points: Vec3[]): number => {
  if (points.length < 2) return 1;
  const nearest = points.map((point, index) => {
    let best = Infinity;
    for (let other = 0; other < points.length; other++) {
      if (other === index) continue;
      const d = distance(point, points[other]);
      if (d < best) best = d;
    }
    return best;
  });
  const median = percentile(nearest, 0.5);
  return median > 1e-6 ? median : 1;
};

/**
 * Ballungen finden — dichtebasiert (Single-Link) mit einer Schwelle aus dem Maßstab der Wolke.
 *
 * **Kein k-Means:** ein Kraftgraph mit einem Nabenknoten liefert dort eine Riesenballung und lauter
 * Einzelgänger, und die Zahl k müsste geraten werden. Single-Link verbindet, was näher beieinander
 * liegt als `factor × Median-Nachbarabstand`, und die Zahl der Ballungen sagt die Wolke selbst.
 */
export const clusterPoints = (points: JourneyPoint[], factor = 2.2): Cluster[] => {
  if (!points.length) return [];
  const threshold = medianNearestNeighbourDistance(points) * factor;

  // Union-Find über alle Paare unterhalb der Schwelle.
  const parent = points.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root];
    while (parent[index] !== root) {
      const next = parent[index];
      parent[index] = root;
      index = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (distance(points[i], points[j]) <= threshold) union(i, j);
    }
  }

  const byRoot = new Map<number, number[]>();
  for (let index = 0; index < points.length; index++) {
    const root = find(index);
    const members = byRoot.get(root);
    if (members) members.push(index);
    else byRoot.set(root, [index]);
  }

  // `Array.from` statt Spread und ein ausdrücklicher Typ: das Portal kompiliert auf ES5, wo ein
  // gespreizter Iterator `downlevelIteration` verlangt.
  return Array.from(byRoot.values())
    .map((members: number[]) => {
      const positions = members.map(index => points[index]);
      const centroid = centroidOf(positions);
      const distances = positions.map(position => distance(position, centroid));
      const byType = new Map<string, number>();
      for (const index of members) {
        byType.set(points[index].type, (byType.get(points[index].type) ?? 0) + 1);
      }
      const dominant = Array.from(byType.entries()).sort((a, b) => b[1] - a[1])[0];
      return {
        members,
        centroid,
        // Das 95. Perzentil, nicht das Maximum: ein einziger weit außen liegender Knoten
        // verdoppelte sonst den Radius und das Schiff umrundete leeren Raum.
        radius: Math.max(percentile(distances, 0.95), threshold * 0.5),
        label: dominant ? dominant[0] : '',
        count: members.length,
      };
    })
    .sort((a, b) => b.count - a.count);
};

/**
 * Die Reihenfolge der Ballungen: nächster Nachbar, dann 2-opt.
 *
 * Bei einer Handvoll Schwerpunkten ist das praktisch die optimale Tour und kostet nichts. Der Zweck
 * ist nicht Kürze an sich, sondern dass die Fahrt nicht zweimal durch dieselbe Gegend führt.
 */
export const orderClusters = (clusters: Cluster[]): number[] => {
  if (clusters.length <= 2) return clusters.map((_, index) => index);
  const remaining = new Set(clusters.map((_, index) => index));
  const order: number[] = [0];
  remaining.delete(0);
  while (remaining.size) {
    const last = clusters[order[order.length - 1]].centroid;
    let best = -1;
    let bestDistance = Infinity;
    // Ein `for…of` über ein Set ist derselbe Fall — auch hier `Array.from`.
    for (const index of Array.from(remaining)) {
      const d = distance(last, clusters[index].centroid);
      if (d < bestDistance) {
        bestDistance = d;
        best = index;
      }
    }
    order.push(best);
    remaining.delete(best);
  }

  const tourLength = (route: number[]): number =>
    route
      .slice(1)
      .reduce(
        (sum, index, position) =>
          sum + distance(clusters[route[position]].centroid, clusters[index].centroid),
        0
      );

  let improved = true;
  let guard = 0;
  while (improved && guard++ < 50) {
    improved = false;
    for (let i = 1; i < order.length - 1; i++) {
      for (let j = i + 1; j < order.length; j++) {
        const candidate = [...order.slice(0, i), ...order.slice(i, j + 1).reverse(), ...order.slice(j + 1)];
        if (tourLength(candidate) + 1e-9 < tourLength(order)) {
          order.splice(0, order.length, ...candidate);
          improved = true;
        }
      }
    }
  }
  return order;
};

/** Ein Wegpunkt der Flugbahn, mit dem, worauf die Kamera dabei schaut. */
export interface Waypoint {
  position: Vec3;
  /** Der Schwerpunkt, um den gerade geflogen wird — oder null im Transit. */
  focus: Vec3 | null;
  /** Zu welcher Ballung dieser Abschnitt gehört (Index in der Ballungsliste). */
  cluster: number | null;
}

/**
 * Die Bahn: erst eine Umrundung des Ganzen, dann Ballung für Ballung ein Bogen um ihren Schwerpunkt.
 *
 * Die große Runde ist die Übersichtsaufnahme und braucht keine Parameter — Radius aus der robusten
 * Ausdehnung der Wolke, Ebene aus den beiden Achsen mit der größten Streuung. Die kleinen Bögen
 * zeigen dann, woraus die Ballungen bestehen.
 */
export const buildJourneyPath = (
  points: JourneyPoint[],
  clusters: Cluster[],
  options: { orbitSamples?: number; grandSamples?: number; margin?: number } = {}
): Waypoint[] => {
  if (!points.length) return [];
  const orbitSamples = options.orbitSamples ?? 24;
  const grandSamples = options.grandSamples ?? 48;
  const margin = options.margin ?? 1.6;

  const centre = centroidOf(points);
  const spread = percentile(points.map(point => distance(point, centre)), 0.95);
  const radius = Math.max(spread * margin, 1);

  /**
   * Eine Runde in der Waagerechten, die Kamera darüber — der Blick geht 45° hinab.
   *
   * Vorher lag die Bahn in der Ebene der beiden Achsen mit der größten Streuung. Das ist als
   * Übersichtsaufnahme gut gedacht, hat aber keine feste Beziehung zum Oben der Kamera: Je nachdem,
   * wie die Simulation die Wolke gerade hingelegt hatte, flog man mal darüber, mal mittendurch,
   * mal senkrecht daran vorbei. Eine waagerechte Runde mit der Höhe des Radius steht dagegen
   * **immer** gleich zur Wolke — `tan 45° = 1`, die Höhe ist der Radius —, und was unter der Kamera
   * liegt, ist damit auch im Bild.
   */
  const arc = (around: Vec3, ringRadius: number, angle: number, wobble: number): Vec3 => ({
    x: around.x + Math.cos(angle) * ringRadius,
    // Die leichte Höhenschwankung nimmt der Runde den Drehteller, ohne die 45° aufzugeben.
    y: around.y + ringRadius * (1 + Math.sin(angle * 2) * wobble),
    z: around.z + Math.sin(angle) * ringRadius,
  });

  const path: Waypoint[] = [];
  for (let i = 0; i < grandSamples; i++) {
    const angle = (i / grandSamples) * Math.PI * 2;
    path.push({ position: arc(centre, radius, angle, 0.12), focus: centre, cluster: null });
  }

  for (const clusterIndex of orderClusters(clusters)) {
    const cluster = clusters[clusterIndex];
    const orbitRadius = Math.max(cluster.radius * 2.2, radius * 0.12);
    for (let i = 0; i < orbitSamples; i++) {
      const angle = (i / orbitSamples) * Math.PI * 2;
      path.push({
        position: arc(cluster.centroid, orbitRadius, angle, 0.18),
        focus: cluster.centroid,
        cluster: clusterIndex,
      });
    }
  }

  return path;
};

/**
 * Die Bahn aus der Wolke herausdrücken.
 *
 * Kommt ein Bahnpunkt einem Knoten näher als `minDistance`, wird er nach außen geschoben. Ohne das
 * steckt die Kamera irgendwann **in** einer Kugel: das Bild wird schwarz, und das sieht aus wie ein
 * Absturz — es ist einer.
 */
export const pushOutside = (
  path: Waypoint[],
  points: JourneyPoint[],
  minDistance: number
): Waypoint[] =>
  path.map(waypoint => {
    let position = waypoint.position;
    for (const point of points) {
      const gap = distance(position, point);
      if (gap >= minDistance || gap < 1e-6) continue;
      const away = normalize(sub(position, point));
      position = add(point, scale(away, minDistance));
    }
    return { ...waypoint, position };
  });

/** Catmull-Rom durch die Wegpunkte — die Ecken zwischen den Abschnitten verschwinden. */
export const smoothPath = (path: Vec3[], samplesPerSegment = 6): Vec3[] => {
  if (path.length < 3) return path;
  const out: Vec3[] = [];
  const at = (index: number): Vec3 => path[Math.min(Math.max(index, 0), path.length - 1)];
  for (let i = 0; i < path.length - 1; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    for (let s = 0; s < samplesPerSegment; s++) {
      const t = s / samplesPerSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      const component = (a: number, b: number, c: number, d: number) =>
        0.5 *
        ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push({
        x: component(p0.x, p1.x, p2.x, p3.x),
        y: component(p0.y, p1.y, p2.y, p3.y),
        z: component(p0.z, p1.z, p2.z, p3.z),
      });
    }
  }
  out.push(path[path.length - 1]);
  return out;
};

/**
 * Gleichmäßig nach **Bogenlänge** neu abtasten.
 *
 * Das ist der Unterschied zwischen Raumschiff und Diashow: ohne diesen Schritt wird das Schiff auf
 * kurzen Abschnitten schnell und auf langen langsam — genau umgekehrt zur Erwartung.
 */
export const resampleByArcLength = (path: Vec3[], steps: number): Vec3[] => {
  if (path.length < 2 || steps < 2) return path;
  const cumulative: number[] = [0];
  for (let i = 1; i < path.length; i++) {
    cumulative.push(cumulative[i - 1] + distance(path[i - 1], path[i]));
  }
  const total = cumulative[cumulative.length - 1];
  if (total < 1e-9) return path;

  const out: Vec3[] = [];
  let cursor = 0;
  for (let step = 0; step < steps; step++) {
    const target = (step / (steps - 1)) * total;
    while (cursor < cumulative.length - 2 && cumulative[cursor + 1] < target) cursor++;
    const segment = cumulative[cursor + 1] - cumulative[cursor];
    const t = segment < 1e-9 ? 0 : (target - cumulative[cursor]) / segment;
    out.push(add(path[cursor], scale(sub(path[cursor + 1], path[cursor]), t)));
  }
  return out;
};

/** Der räumlich nächste Knoten zu einer Position — das ist der, der hervorgehoben wird. */
export const nearestPoint = (
  position: Vec3,
  points: JourneyPoint[]
): { point: JourneyPoint; distance: number } | null => {
  let best: JourneyPoint | null = null;
  let bestDistance = Infinity;
  for (const point of points) {
    const d = distance(position, point);
    if (d < bestDistance) {
      bestDistance = d;
      best = point;
    }
  }
  return best ? { point: best, distance: bestDistance } : null;
};

/** Zwischen zwei Punkten interpolieren. */
export const lerpVec = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
});

/**
 * Die Stelle auf der Bahn zu einem **gebrochenen** Fortschritt.
 *
 * Ein Bahnpunkt je Bild wäre bei 60 Bildern in der Sekunde ein Sprung von Stützstelle zu
 * Stützstelle — schnell und ruckelig. Mit einem Bruchteil je Bild wird dazwischen interpoliert,
 * und das Tempo ist eine Zahl statt einer Frage der Bildrate.
 *
 * Der Fortschritt läuft **um**: die Reise endet nicht von selbst, sie wird beendet.
 */
export const pathPositionAt = (path: Waypoint[], progress: number): Waypoint => {
  if (!path.length) return { position: { x: 0, y: 0, z: 0 }, focus: null, cluster: null };
  if (path.length === 1) return path[0];
  const wrapped = ((progress % path.length) + path.length) % path.length;
  const index = Math.floor(wrapped);
  const next = (index + 1) % path.length;
  const t = wrapped - index;
  return {
    position: lerpVec(path[index].position, path[next].position, t),
    // Ziel und Ballung wechseln an der Stützstelle, nicht dazwischen: ein interpolierter Blickpunkt
    // zwischen zwei Ballungen zeigt auf nichts.
    focus: path[index].focus,
    cluster: path[index].cluster,
  };
};

/**
 * Ob der hervorgehobene Knoten gewechselt werden soll.
 *
 * Ohne Hysterese wechselt er im Flug mehrmals je Sekunde: die Beschriftung flackert, und weil die
 * Kugel ihre Größe träger annimmt als der Text sich ändert, gehören beide sichtbar nicht mehr
 * zusammen. Ein Wechsel braucht deshalb entweder **Zeit** (Mindeststandzeit) oder ein **deutlich**
 * näheres Ziel — „ein bisschen näher" ist bei einer fliegenden Kamera kein Grund.
 */
export const shouldSwitchHighlight = (
  current: { id: string; distance: number } | null,
  candidateId: string,
  candidateDistance: number,
  elapsedMs: number,
  options: { minDwellMs?: number; closerFactor?: number } = {},
): boolean => {
  if (!current) return true;
  if (current.id === candidateId) return false;
  const minDwellMs = options.minDwellMs ?? 1500;
  const closerFactor = options.closerFactor ?? 0.5;
  if (elapsedMs >= minDwellMs) return true;
  return candidateDistance < current.distance * closerFactor;
};

/**
 * Wohin die Kamera schaut: **immer auf die Wolke, nie ins Leere.**
 *
 * Der Vorgänger mischte den Schwerpunkt mit dem Punkt voraus und schaltete per Skalarprodukt hart
 * auf „nur voraus" um, sobald der Blick hinter die Flugrichtung fiel. Beim Umrunden einer Ballung
 * steht die Bewegung aber **senkrecht** auf dem Blick — das Skalarprodukt liegt dort um die Null
 * und wechselt mehrmals je Umlauf das Vorzeichen. Jeder Wechsel warf den Blick vom Schwerpunkt
 * weg auf einen Punkt der Bahn: das war der Ruck, und wo die Bahn nach außen führte, war es der
 * Moment, in dem der Graph aus dem Bild flog.
 *
 * Statt einer Fallunterscheidung jetzt eine stetige Neigung. Der Blick geht grundsätzlich auf den
 * Schwerpunkt — einen Punkt, der **in** der Wolke liegt — und wird nur um ein festes Maß in die
 * Flugrichtung geneigt, damit es nach Fliegen aussieht und nicht nach Drehteller.
 *
 * Die Neigung ist von selbst begrenzt: Bei zwei Einheitsvektoren weicht `f + lean·t` höchstens um
 * `asin(lean)` von `f` ab — bei 0,35 also gut 20°. Zeigt die Bewegung genau vom Schwerpunkt weg,
 * bleibt `f + lean·t` immer noch auf ihn gerichtet, nur kürzer. Es gibt keine Lage, in der die
 * Kamera an der Wolke vorbeisieht.
 */
export const cockpitLookAt = (
  position: Vec3,
  ahead: Vec3,
  focus: Vec3 | null,
  lean = 0.35,
): Vec3 => {
  if (!focus) return ahead;
  const toFocus = sub(focus, position);
  const reach = length(toFocus);
  // Steht die Kamera auf dem Schwerpunkt, gibt es keine Blickrichtung zu ihm — dann voraus.
  if (reach < 1e-6) return ahead;
  const towards = scale(toFocus, 1 / reach);
  const travel = normalize(sub(ahead, position));
  const direction = normalize(add(towards, scale(travel, Math.min(Math.max(lean, 0), 0.9))));
  // Auf der Entfernung des Schwerpunkts bleiben: Ein Blickpunkt, der mit der Neigung auch näher
  // oder ferner rückte, ließe die Wolke atmen.
  return add(position, scale(direction, reach));
};

/**
 * Einen Wert weich nachziehen — exponentielle Dämpfung, bildratenunabhängig.
 *
 * Der Blickpunkt der Reise springt an drei Stellen: beim Wechsel der Ballung (ein anderer
 * Schwerpunkt), beim Umschalten zwischen „auf die Ballung" und „nach vorn", und an jeder
 * Stützstelle. Jeder dieser Sprünge ist ein Ruck im Bild. Nachgezogen statt gesetzt, verschwinden
 * alle drei — und weil die Dämpfung über die verstrichene Zeit rechnet, sieht sie bei 30 wie bei
 * 120 Bildern in der Sekunde gleich aus.
 *
 * @param halfLifeMs Zeit, in der die Hälfte des Abstands aufgeholt wird.
 */
export const dampVec = (current: Vec3, target: Vec3, elapsedMs: number, halfLifeMs = 220): Vec3 => {
  if (halfLifeMs <= 0) return target;
  const t = 1 - Math.pow(0.5, Math.max(elapsedMs, 0) / halfLifeMs);
  return lerpVec(current, target, Math.min(Math.max(t, 0), 1));
};
